import { isPlatformServer } from '@angular/common';
import {
  DOCUMENT,
  Inject,
  inject,
  Injectable,
  PLATFORM_ID,
  REQUEST,
} from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { environment } from '@src/environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class SeoMetaService {
  private ogImageSrcSubject = new BehaviorSubject<string>(
    'https://upload.wikimedia.org/wikipedia/commons/4/41/Acba-logo.png',
  );
  ogImageSrc$: Observable<string> = this.ogImageSrcSubject.asObservable();

  get ogImageSrc(): string {
    return this.ogImageSrcSubject.value;
  }

  setOgImageSrc(value: string): void {
    this.ogImageSrcSubject.next(value);
  }

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private router: Router,
    private title: Title,
    private meta: Meta,
  ) {}

  request = inject(REQUEST, { optional: true });
  document = inject(DOCUMENT);

  apply(data: any, title?: string) {
    this.appendCanonical();

    if (!data) return;

    this.title.setTitle(data.title || title);

    this.meta.updateTag({ name: 'title', content: data.title || title || '' });
    this.meta.updateTag({
      name: 'description',
      content: data.description || '',
    });
    this.meta.updateTag({ name: 'og:type', content: 'website' });
    this.meta.updateTag({ name: 'site_name', content: 'ACBA bank' });
    this.meta.updateTag({ property: 'og:title', content: data.title || '' });
    this.meta.updateTag({
      property: 'og:description',
      content: data.description || '',
    });
    this.meta.updateTag({
      name: 'og:image',
      content: this.ogImageSrcSubject.value,
    });
    this.meta.updateTag({ name: 'og:image:width', content: '1200' });
    this.meta.updateTag({ name: 'og:image:height', content: '630' });
    // this.meta.updateTag({ name: 'og:locale', content: 'hy_HY' });
  }

  appendCanonical() {
    const canonicalLink = this.createCanonicalUrl();

    let canonicalLinkElement = this.document.querySelector(
      'link[rel="canonical"]',
    ) as HTMLLinkElement;

    if (!canonicalLinkElement) {
      canonicalLinkElement = this.document.createElement('link');
      canonicalLinkElement.setAttribute('rel', 'canonical');
      this.document.head.appendChild(canonicalLinkElement);
    }

    canonicalLinkElement.setAttribute('href', canonicalLink);

    this.meta.updateTag({ name: 'og:url', content: canonicalLink });
    this.meta.updateTag({ name: 'twitter:url', content: canonicalLink });
  }

  createCanonicalUrl(): string {
    const path = this.router.url;

    let origin: string;

    if (isPlatformServer(this.platformId) && this.request) {
      const headers = this.request.headers as Headers | undefined;

      const proto = (headers?.get('x-forwarded-proto') as string) || 'https';

      const host =
        (headers?.get('x-forwarded-host') as string) || headers?.get('host');

      origin = host ? `${proto}://${host}` : '';
    } else {
      origin = this.document.location.origin;
    }

    return `${environment.origin}${path}`;
  }
}
